import { serviceRequestPriorityChoices, serviceRequestStatusChoices, serviceRequestTypeChoices } from '@/components/global/options/selectOptions.jsx';
import { formatTimePassed } from '@/helpers/date/formatTimePassed.js';
import { formatIntervalSince } from '@/helpers/date/formatTimeInterval.js';
import { defaultDateFieldOptions } from '@/constants/Date.jsx';

const joinParts = parts => parts.filter(Boolean).join(' | ');

const formatCollectionPointValue = record => {
    const collectionPoint = record?.collectionPoint;
    const collectionPointAddressParts = collectionPoint?.address
        ? [collectionPoint.address.address, collectionPoint.address.postalCode ? `${collectionPoint.address.postalCode} ${collectionPoint.address.city}` : '']
        : [];
    const collectionPointAddress = collectionPointAddressParts.filter(Boolean).join(', ');
    const collectionPointPhones = [collectionPoint?.contact?.contactPhoneNumber, collectionPoint?.contact?.employeePhoneNumber].filter(Boolean).join(', ');
    return joinParts([collectionPoint?.customId, collectionPoint?.contractor?.name, collectionPointAddress, collectionPointPhones]);
};

const formatStatusLabel = (record, translate) => {
    if (record?.status === 'new') {
        return translate('resources.serviceRequest.labels.statusNew');
    }

    return serviceRequestStatusChoices(translate).find(choice => choice.id === record?.status)?.name ?? translate('resources.device.fields.notSpecified');
};

const formatStatusValue = (record, translate) => {
    const statusLabel = formatStatusLabel(record, translate);
    const statusTime = record?.updatedAt
        ? record?.status === 'new'
            ? formatIntervalSince(record.updatedAt)
            : formatTimePassed(record.updatedAt, translate)
        : '';
    return joinParts([statusLabel, statusTime]);
};

const formatDeviceStateValue = (record, translate, deviceStates) => {
    const deviceStateLabel = deviceStates.find(state => state.id === record?.device?.state)?.name ?? '';
    let deviceUptime = '';
    if (record?.device?.uptime !== null && record?.device?.uptime !== undefined) {
        deviceUptime = `${record.device.uptime}% ${translate('common.uptime')}`;
    }
    let deviceTransactions = '';
    if (record?.device?.transactions !== null && record?.device?.transactions !== undefined) {
        deviceTransactions = `${record.device.transactions} ${translate('common.transactionsCount')}`;
    }
    return joinParts([deviceStateLabel, deviceUptime, deviceTransactions]);
};

const formatUsersValue = (record, translate) => {
    const coordinatorName = record?.coordinatorId ? `${record.coordinator?.firstName ?? ''} ${record.coordinator?.lastName ?? ''}`.trim() : '';
    const servicemanName = record?.servicemanId ? `${record.serviceman?.firstName ?? ''} ${record.serviceman?.lastName ?? ''}`.trim() : '';
    return [
        `${translate('resources.serviceRequest.labels.coordinator')}: ${coordinatorName || translate('resources.device.fields.notSpecified')}`,
        `${translate('resources.serviceRequest.labels.serviceman')}: ${servicemanName || translate('resources.device.fields.notSpecified')}`,
    ].join(' | ');
};

const formatInspectionDate = (value, translate) => {
    if (!value) {
        return translate('resources.serviceInspection.labels.noTerm');
    }
    return new Date(value).toLocaleString('pl-PL', defaultDateFieldOptions);
};

export const mapServiceInspectionExportRow = (record, translate, deviceStates) => {
    let formattedId = '';
    if (record?.customId !== null && record?.customId !== undefined) {
        formattedId = `#${record.customId.toString().padStart(6, '0')}`;
    }
    const priorityLabel = serviceRequestPriorityChoices(translate).find(choice => choice.id === record?.priority)?.name ?? '';
    const typeLabel = serviceRequestTypeChoices(translate).find(choice => choice.id === record?.type)?.name ?? '';
    const commentCount = record?.changeCount ?? record?.visibleChangeCount ?? 0;
    const requestValue = joinParts([formattedId, priorityLabel, typeLabel, `${commentCount}`]);

    return {
        [translate('resources.serviceRequest.labels.requestTitle')]: requestValue,
        [translate('common.collectionPoint')]: formatCollectionPointValue(record),
        [translate('resources.serviceInspection.labels.dueDate')]: formatInspectionDate(record?.dueDate, translate),
        [translate('resources.serviceInspection.labels.plannedDate')]: formatInspectionDate(record?.scheduledDate, translate),
        [translate('resources.device.fields.serialNumber')]: record?.device?.serialNumber ?? '',
        [translate('common.status')]: formatStatusValue(record, translate),
        [translate('resources.serviceRequest.labels.deviceStateTitle')]: formatDeviceStateValue(record, translate, deviceStates),
        [translate('resources.serviceRequest.labels.coordinator') + ' / ' + translate('resources.serviceRequest.labels.serviceman')]: formatUsersValue(
            record,
            translate,
        ),
    };
};
